/**
 * The Funrooms' printed surfaces.
 *
 * Two things on this level are drawn rather than modelled, for the same reason
 * the graffiti in `propdecals.ts` is: they are paper, and paper does not
 * survive being built out of boxes. A banner is a ribbon that sags between two
 * fixings with its lettering following the sag — modelled, it is a plank with a
 * word on it — and confetti is two hundred pieces of card, which is two hundred
 * quads nobody can afford.
 *
 * Both follow the atlas discipline of `propdecals.ts`: one canvas per kind,
 * variants addressed as rows by UV, so six banners cost one upload and one
 * material. The row-building code is deliberately a local copy rather than a
 * shared export — `propdecals.ts` belongs to another part of the catalogue and
 * is not this file's to widen.
 *
 * The wording is of two kinds and neither is addressed to anybody. Most of it is
 * stock celebration — the boxed banners a venue keeps in a cupboard and puts up
 * for whoever booked — and the rest is the building naming its own zones, which
 * is the only signage a soft-play centre actually owns. Nobody's name is on any
 * of it. That is what stops a banner reading as a person and starts it reading
 * as stock, and the zone boards are the one thing in here that was hung to be
 * useful rather than to be nice.
 */

import * as THREE from 'three';
import { clamp01, TAU } from '../core/math';
import { makeRNG } from '../core/rng';
import type { RNG } from '../core/types';
import type { AtlasEntry, DecalAtlas } from './propdecals';

/** Texels trimmed off each row so mip levels never bleed one row into the next. */
const INSET = 1;

/**
 * Paint `count` rows onto one canvas and hand back the UV rectangle of each.
 * `paint` draws in row-local coordinates and returns the width it used, which
 * is what sets that variant's aspect ratio and so how wide its quad comes out.
 */
function buildAtlas(
  width: number,
  rowHeight: number,
  count: number,
  seed: number,
  paint: (ctx: CanvasRenderingContext2D, index: number, w: number, h: number, rng: RNG) => number
): DecalAtlas {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = rowHeight * count;
  const ctx = canvas.getContext('2d')!;
  const rng = makeRNG(seed);
  const entries: AtlasEntry[] = [];

  for (let i = 0; i < count; i++) {
    ctx.save();
    ctx.translate(0, i * rowHeight);
    ctx.beginPath();
    ctx.rect(0, 0, width, rowHeight);
    ctx.clip();
    const used = Math.max(8, Math.min(width, paint(ctx, i, width, rowHeight, rng)));
    ctx.restore();

    const top = i * rowHeight + INSET;
    const bottom = (i + 1) * rowHeight - INSET;
    entries.push({
      u0: INSET / width,
      u1: (used - INSET) / width,
      // Canvas y runs down; texture v runs up, and three flips the upload.
      v0: 1 - bottom / canvas.height,
      v1: 1 - top / canvas.height,
      aspect: (used - 2 * INSET) / (rowHeight - 2 * INSET),
    });
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  // Banners are nearly always seen from an angle, the same as every other wall
  // mark in the building.
  texture.anisotropy = 4;
  return {
    texture,
    entries,
    dispose(): void {
      texture.dispose();
    },
  };
}

/** `#rrggbb` plus an alpha, as Canvas2D wants it. */
function rgba(hex: string, alpha: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${clamp01(alpha).toFixed(3)})`;
}

/* ------------------------------------------------------------------ *
 * Banners
 * ------------------------------------------------------------------ */

/** A point on a quadratic Bezier, and the direction it is heading. */
function bezier(
  t: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number
): { x: number; y: number; a: number } {
  const u = 1 - t;
  return {
    x: u * u * ax + 2 * u * t * bx + t * t * cx,
    y: u * u * ay + 2 * u * t * by + t * t * cy,
    a: Math.atan2(2 * u * (by - ay) + 2 * t * (cy - by), 2 * u * (bx - ax) + 2 * t * (cx - bx)),
  };
}

/** How a banner is hanging by the time anybody sees it. */
type BannerForm =
  /** Both fixings holding, the swag even. */
  | 'flat'
  /** One end let go, so the whole run hangs off the other. */
  | 'droop'
  /** A bite out of the middle. Whatever took it is not in the room. */
  | 'torn'
  /** No lettering at all: triangles on a string, which is most party bunting. */
  | 'bunting';

interface BannerSpec {
  text: string;
  form: BannerForm;
  /** Ribbon colour. */
  cloth: number;
  /** Fraction of the atlas row the drawn banner uses; sets the quad's width. */
  span: number;
}

/**
 * What the venue had in the cupboard, and what it hung permanently.
 *
 * Nothing here is addressed to anybody and nothing refers to a date: the party
 * banners are stock signage bought by the box, and the flat ones are hung
 * exactly as square as a bored member of staff would manage.
 *
 * The last two are different. They are the venue's own zone banners, in the
 * colours it colour-coded its areas with — the level is a field of bays joined
 * by tunnels and nothing else tells you which bay you are standing in, so these
 * are the closest the building comes to admitting it has a plan. Printed vinyl,
 * hung the day the place opened and left up, which is why they are the two that
 * are hanging straight.
 */
const BANNERS: readonly BannerSpec[] = [
  { text: 'HAPPY BIRTHDAY', form: 'flat', cloth: 0xd8342c, span: 1.0 },
  { text: 'CONGRATULATIONS', form: 'flat', cloth: 0x2f6fd0, span: 1.0 },
  { text: 'WELCOME', form: 'flat', cloth: 0x3fa34a, span: 0.66 },
  { text: 'HAPPY BIRTHDAY', form: 'droop', cloth: 0xf2b91f, span: 0.92 },
  { text: 'CONGRATULATIONS', form: 'torn', cloth: 0xd8342c, span: 1.0 },
  { text: '', form: 'bunting', cloth: 0xe8752a, span: 0.86 },
  { text: 'SOFT PLAY', form: 'flat', cloth: 0x3fa34a, span: 0.7 },
  { text: 'TODDLER AREA', form: 'flat', cloth: 0x2f6fd0, span: 0.82 },
];

/**
 * The wall lining, sharing the banner atlas.
 *
 * A soft-play hall has no bare wall anywhere in it: the bottom two metres are
 * quilted crash padding in the same primaries as the frame, and above that is
 * more netting. Both are printed surfaces at this distance — quilting is a
 * pattern of stitched seams and a net is a grid of string, and neither survives
 * being modelled out of boxes any better than a banner does — so they are rows
 * of this atlas rather than a kind of their own, and `PROP_DEFS.banner` carries
 * a mount range wide enough for a dado as well as for a high-level swag.
 *
 * This is what stops a bay reading as a blue-carpet hall with equipment in it.
 * The frame fills the volume; the lining is what the walls of a soft-play
 * centre actually are, and it costs nothing at all in collision.
 */
interface LiningSpec {
  kind: 'padding' | 'net';
  /** Panel colours, left to right, repeating. */
  colours: readonly number[];
  /** Metres tall the quad is drawn. Sets the width too, via the row aspect. */
  metres: number;
  /** Fraction of the atlas row used; with `metres`, this is the real width. */
  span: number;
}

const LINING: readonly LiningSpec[] = [
  { kind: 'padding', colours: [0xd8342c, 0xf2b91f, 0x2f6fd0], metres: 1.16, span: 0.78 },
  { kind: 'padding', colours: [0x3fa34a, 0xe8752a, 0xf2b91f], metres: 1.02, span: 0.78 },
  { kind: 'net', colours: [0x2f6fd0, 0xf2b91f], metres: 1.72, span: 0.56 },
  { kind: 'net', colours: [0x3fa34a, 0xd8342c], metres: 1.94, span: 0.5 },
];

/**
 * Height in metres of each atlas row's quad. Banners are all the same ribbon;
 * the lining panels are each their own size, because a dado and a net screen
 * are not the same object drawn twice.
 */
export const BANNER_METRES: readonly number[] = [
  ...BANNERS.map(() => 0.62),
  ...LINING.map((l) => l.metres),
];

/** How many banner variants the atlas carries, lining included. */
export const BANNER_COUNT = BANNERS.length + LINING.length;

/**
 * First atlas row that is wall lining rather than a hung banner. Rows below it
 * are ribbons; rows from it up are the building's own surface. Both the model
 * and `world/gen/funrooms.ts` address the atlas by index, so the split is
 * exported rather than counted twice.
 */
export const BANNER_LINING_FROM = BANNERS.length;

/*
 * `world/gen/funrooms.ts` hangs the lining itself, and addresses these rows by
 * literal index — 8 and 9 for the padding, 10 and 11 for the screens — because
 * nothing under `world/` may import from the renderer. Same arrangement, and the
 * same hazard, as `SIGN_ROW.exit` in `propdecals.ts`: **add a banner and those
 * literals are wrong.** Add new rows at the end of `LINING` rather than
 * anywhere else, or fix `LINING_ROW` and `SCREEN_ROW` over there to match.
 */

/** Bunting flag colours, cycled along the string. */
const FLAG_COLOURS: readonly number[] = [0xd8342c, 0xf2b91f, 0x3fa34a, 0x2f6fd0];

/**
 * Draw every banner variant onto one canvas.
 *
 * `tint` pulls each colour toward the level it is hanging in, the same way
 * `makeSignAtlas` does, so the same red ribbon is warm here and would be sour
 * anywhere else. The background stays transparent and the material alpha-tests
 * it, so the shape of the swag is the shape of the prop — no quad edges, and no
 * transparency sort for a prop there may be a dozen of.
 */
export function makeBannerAtlas(tint: (hex: number) => string): DecalAtlas {
  const ROW = 160;
  const WIDTH = 640;

  return buildAtlas(WIDTH, ROW, BANNER_COUNT, 0x71c4e3, (ctx, i, w, h, rng) => {
    if (i >= BANNERS.length) return paintLining(ctx, LINING[i - BANNERS.length]!, w, h, tint, rng);
    const spec = BANNERS[i]!;
    const used = Math.round(w * spec.span);
    const pad = 14;
    const x0 = pad;
    const x1 = used - pad;
    const cloth = tint(spec.cloth);
    // The lettering is printed, not written, so it is flat cream rather than
    // marker: this is the one surface in the building that was made to be read.
    const ink = tint(0xfdf4e2);
    const cord = tint(0x8c7a56);

    // Where each fixing sits, and how far the middle falls below them. A droop
    // is one nail out, so that end drops most of the row.
    const yLeft = spec.form === 'droop' ? h * 0.2 : h * 0.28;
    const yRight = spec.form === 'droop' ? h * 0.74 : h * 0.28;
    const sag = spec.form === 'droop' ? h * 0.14 : h * 0.26;
    const midX = (x0 + x1) / 2;
    const midY = (yLeft + yRight) / 2 + sag * 2;

    // The cord it hangs from, drawn first so the ribbon covers where it enters.
    ctx.strokeStyle = cord;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(x0, yLeft);
    ctx.quadraticCurveTo(midX, midY - (spec.form === 'bunting' ? 0 : h * 0.1), x1, yRight);
    ctx.stroke();

    if (spec.form === 'bunting') {
      paintBunting(ctx, x0, yLeft, midX, midY, x1, yRight, h, tint, rng);
      return used;
    }

    const band = h * 0.4;
    paintSwag(ctx, x0, yLeft, midX, midY, x1, yRight, band, cloth);
    paintLettering(ctx, spec.text, x0, yLeft, midX, midY, x1, yRight, band, ink, rng);

    // Creases from being folded back into its box, and the shadow the far side
    // of a swag always has along its lower edge.
    ctx.save();
    ctx.globalAlpha = 0.16;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    for (let c = 0; c < 4; c++) {
      const t = rng.range(0.12, 0.88);
      const p = bezier(t, x0, yLeft, midX, midY, x1, yRight);
      const nx = Math.sin(p.a);
      const ny = -Math.cos(p.a);
      ctx.beginPath();
      ctx.moveTo(p.x - nx * band * 0.5, p.y - ny * band * 0.5);
      ctx.lineTo(p.x + nx * band * 0.5 + rng.range(-4, 4), p.y + ny * band * 0.5);
      ctx.stroke();
    }
    ctx.restore();

    if (spec.form === 'torn') {
      // A bite out of the middle, taken with `destination-out` so the hole is a
      // hole in the alpha rather than a patch of some other colour.
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000000';
      ctx.beginPath();
      const start = 0.42;
      const end = 0.66;
      for (let s = 0; s <= 8; s++) {
        const p = bezier(start + ((end - start) * s) / 8, x0, yLeft, midX, midY, x1, yRight);
        const nx = Math.sin(p.a);
        const ny = -Math.cos(p.a);
        const reach = band * rng.range(0.5, 1.1);
        const px = p.x + nx * reach;
        const py = p.y + ny * reach;
        if (s === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      for (let s = 8; s >= 0; s--) {
        const p = bezier(start + ((end - start) * s) / 8, x0, yLeft, midX, midY, x1, yRight);
        const nx = Math.sin(p.a);
        const ny = -Math.cos(p.a);
        const reach = band * rng.range(0.1, 0.5);
        ctx.lineTo(p.x - nx * reach, p.y - ny * reach);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    return used;
  });
}

/** The ribbon itself: a band of even thickness following the sag. */
function paintSwag(
  ctx: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  mx: number,
  my: number,
  x1: number,
  y1: number,
  band: number,
  cloth: string
): void {
  const STEPS = 26;
  ctx.beginPath();
  for (let s = 0; s <= STEPS; s++) {
    const p = bezier(s / STEPS, x0, y0, mx, my, x1, y1);
    const nx = Math.sin(p.a);
    const ny = -Math.cos(p.a);
    const px = p.x - nx * band * 0.5;
    const py = p.y - ny * band * 0.5;
    if (s === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  for (let s = STEPS; s >= 0; s--) {
    const p = bezier(s / STEPS, x0, y0, mx, my, x1, y1);
    const nx = Math.sin(p.a);
    const ny = -Math.cos(p.a);
    // The lower edge is scalloped, which is how party card is always cut.
    const scallop = band * 0.08 * Math.sin((s / STEPS) * Math.PI * 9);
    ctx.lineTo(p.x + nx * (band * 0.5 + scallop), p.y + ny * (band * 0.5 + scallop));
  }
  ctx.closePath();
  ctx.fillStyle = cloth;
  ctx.fill();

  // A darker wash along the bottom third: a swag is never lit evenly.
  ctx.save();
  ctx.clip();
  const shade = ctx.createLinearGradient(0, y0 - band, 0, my + band);
  shade.addColorStop(0, rgba('#000000', 0));
  shade.addColorStop(1, rgba('#000000', 0.28));
  ctx.fillStyle = shade;
  ctx.fillRect(x0 - band, y0 - band * 2, x1 - x0 + band * 2, my - y0 + band * 4);
  ctx.restore();
}

/** Letters set one at a time along the curve, each turned to the local tangent. */
function paintLettering(
  ctx: CanvasRenderingContext2D,
  text: string,
  x0: number,
  y0: number,
  mx: number,
  my: number,
  x1: number,
  y1: number,
  band: number,
  ink: string,
  rng: RNG
): void {
  if (text.length === 0) return;
  const size = Math.min(band * 0.62, ((x1 - x0) * 1.35) / text.length);
  ctx.font = `bold ${size.toFixed(1)}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = ink;

  // Held clear of both ends so the lettering sits on the cloth rather than on
  // the fixings, and centred on the band rather than on the curve.
  const from = 0.11;
  const to = 0.89;
  for (let c = 0; c < text.length; c++) {
    const ch = text[c]!;
    if (ch === ' ') continue;
    const t = from + ((to - from) * (c + 0.5)) / text.length;
    const p = bezier(t, x0, y0, mx, my, x1, y1);
    ctx.save();
    ctx.translate(p.x + Math.sin(p.a) * band * 0.03, p.y - Math.cos(p.a) * band * 0.03);
    ctx.rotate(p.a + rng.range(-0.02, 0.02));
    ctx.fillText(ch, 0, 0);
    ctx.restore();
  }
}

/**
 * One panel of wall lining, filling its whole row.
 *
 * Unlike a banner this is opaque edge to edge — it is the wall, not something
 * hung on it — so it is drawn as a solid run and the material's alpha test
 * never bites. The netting is the exception: it is mostly hole, and the holes
 * are real holes in the alpha, so the level's own yellow wall shows through the
 * mesh exactly as it would behind a real screen.
 */
function paintLining(
  ctx: CanvasRenderingContext2D,
  spec: LiningSpec,
  w: number,
  h: number,
  tint: (hex: number) => string,
  rng: RNG
): number {
  const used = Math.round(w * spec.span);

  if (spec.kind === 'net') {
    // A padded rail top and bottom, and the mesh between them. The rail is what
    // makes a screen read as fitted rather than as a fishing net.
    const rail = h * 0.09;
    ctx.fillStyle = tint(spec.colours[0]!);
    ctx.fillRect(0, 0, used, rail);
    ctx.fillRect(0, h - rail, used, rail);
    ctx.strokeStyle = tint(spec.colours[1]!);
    ctx.lineWidth = Math.max(2, h * 0.017);
    // Diamond mesh rather than a square grid: a square grid at this scale reads
    // as a window frame, and every soft-play screen is hung on the diagonal.
    const pitch = h * 0.17;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, rail, used, h - rail * 2);
    ctx.clip();
    for (let d = -h; d < used + h; d += pitch) {
      ctx.beginPath();
      ctx.moveTo(d, 0);
      ctx.lineTo(d + h, h);
      ctx.moveTo(d + h, 0);
      ctx.lineTo(d, h);
      ctx.stroke();
    }
    ctx.restore();
    return used;
  }

  // Crash padding: vertical quilted panels, butted, with a capping bead along
  // the top. Colours run in order and repeat, the way a venue buys them.
  const panels = 6;
  const bead = h * 0.075;
  for (let p = 0; p < panels; p++) {
    const x0 = (used * p) / panels;
    const x1 = (used * (p + 1)) / panels;
    ctx.fillStyle = tint(spec.colours[p % spec.colours.length]!);
    ctx.fillRect(x0, 0, x1 - x0 + 1, h);
    // The stitched quilting: two seams down and three across, each one a hollow
    // in the foam rather than a line drawn on it, so it needs both a shadow and
    // a highlight to read at all.
    ctx.save();
    ctx.lineWidth = Math.max(2, h * 0.012);
    for (const [dy, colour, alpha] of [
      [0, '#000000', 0.3],
      [-ctx.lineWidth, '#ffffff', 0.16],
    ] as const) {
      ctx.strokeStyle = rgba(colour, alpha);
      ctx.beginPath();
      for (let s = 1; s < 3; s++) {
        const x = x0 + ((x1 - x0) * s) / 3;
        ctx.moveTo(x, bead + dy);
        ctx.lineTo(x, h + dy);
      }
      for (let s = 1; s < 4; s++) {
        const y = bead + ((h - bead) * s) / 4 + dy;
        ctx.moveTo(x0, y);
        ctx.lineTo(x1, y);
      }
      ctx.stroke();
    }
    ctx.restore();
    // The joint between two panels, and the scuff along the bottom where every
    // child in the building has kicked it. Wiped clean, not ruined.
    ctx.fillStyle = rgba('#000000', 0.24);
    ctx.fillRect(x1 - 1, 0, 2, h);
  }
  ctx.fillStyle = rgba('#000000', 0.22);
  ctx.fillRect(0, 0, used, bead);
  ctx.fillStyle = rgba('#ffffff', 0.1);
  ctx.fillRect(0, bead, used, Math.max(1, h * 0.012));
  ctx.save();
  ctx.globalAlpha = 0.1;
  ctx.fillStyle = '#000000';
  for (let s = 0; s < 14; s++) {
    ctx.fillRect(rng.range(0, used), h - rng.range(0, h * 0.16), rng.range(6, 26), rng.range(2, 5));
  }
  ctx.restore();
  return used;
}

/** Triangles on a string, with one or two of them gone. */
function paintBunting(
  ctx: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  mx: number,
  my: number,
  x1: number,
  y1: number,
  h: number,
  tint: (hex: number) => string,
  rng: RNG
): void {
  const FLAGS = 11;
  const width = h * 0.15;
  const drop = h * 0.4;
  for (let f = 0; f < FLAGS; f++) {
    // Two gone, always the same two, because the atlas is drawn once.
    if (f === 3 || f === 8) continue;
    const t = 0.06 + (0.88 * (f + 0.5)) / FLAGS;
    const p = bezier(t, x0, y0, mx, my, x1, y1);
    const nx = Math.sin(p.a);
    const ny = -Math.cos(p.a);
    // Along the cord, so the flags hang square to it rather than to the row.
    const ax = Math.cos(p.a);
    const ay = Math.sin(p.a);
    ctx.beginPath();
    ctx.moveTo(p.x - ax * width * 0.5, p.y - ay * width * 0.5);
    ctx.lineTo(p.x + ax * width * 0.5, p.y + ay * width * 0.5);
    ctx.lineTo(p.x + nx * drop * rng.range(0.85, 1.05), p.y + ny * drop);
    ctx.closePath();
    ctx.fillStyle = tint(FLAG_COLOURS[f % FLAG_COLOURS.length]!);
    ctx.fill();
    ctx.save();
    ctx.globalAlpha = 0.2;
    ctx.fillStyle = '#000000';
    ctx.fill();
    ctx.restore();
  }
}

/* ------------------------------------------------------------------ *
 * Confetti
 * ------------------------------------------------------------------ */

/**
 * Paper trodden into a hard floor.
 *
 * Drawn as one feathered patch rather than as scattered geometry for the reason
 * every other floor decal here is: two hundred pieces of card is two hundred
 * quads, and at the angle a standing player sees the floor from, all that
 * survives is the density. A quarter of the pieces are drawn faded and greyed —
 * those are the ones that have been walked on, and they are what makes the
 * patch read as old rather than as freshly thrown.
 */
export function makeConfettiTexture(colours: readonly string[], scuff: string, seed: number): THREE.Texture {
  const SIZE = 256;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d')!;
  const rng = makeRNG(seed);

  // The scuffs go down first, so the paper still on top of them reads as paper
  // that was there before somebody walked through.
  ctx.save();
  ctx.strokeStyle = scuff;
  ctx.lineCap = 'round';
  for (let s = 0; s < 7; s++) {
    ctx.globalAlpha = rng.range(0.05, 0.13);
    ctx.lineWidth = rng.range(6, 22);
    const a = rng.range(0, TAU);
    const r = rng.range(SIZE * 0.1, SIZE * 0.34);
    const len = rng.range(SIZE * 0.2, SIZE * 0.5);
    const cx = SIZE / 2 + Math.cos(a) * r;
    const cy = SIZE / 2 + Math.sin(a) * r;
    const dir = rng.range(0, TAU);
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(dir) * len, cy + Math.sin(dir) * len);
    ctx.stroke();
  }
  ctx.restore();

  for (let i = 0; i < 190; i++) {
    // Radius weighted toward the middle so the feather never bisects a dense
    // ring of pieces and leaves a visible edge to the patch.
    const a = rng.range(0, TAU);
    const r = Math.pow(rng.next(), 0.62) * SIZE * 0.46;
    const x = SIZE / 2 + Math.cos(a) * r;
    const y = SIZE / 2 + Math.sin(a) * r;
    const trodden = rng.chance(0.28);

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rng.range(0, TAU));
    ctx.fillStyle = colours[rng.int(0, colours.length - 1)]!;
    ctx.globalAlpha = trodden ? rng.range(0.2, 0.42) : rng.range(0.82, 1);
    const roll = rng.next();
    if (roll < 0.55) {
      // Rectangular cuts, the sort that comes out of a hole punch strip.
      const w = rng.range(3.5, 9);
      const hh = rng.range(2, 3.6);
      ctx.fillRect(-w / 2, -hh / 2, w, hh);
    } else if (roll < 0.86) {
      // Discs, from the punch itself.
      ctx.beginPath();
      ctx.arc(0, 0, rng.range(1.6, 3.2), 0, TAU);
      ctx.fill();
    } else {
      // Curled streamer offcuts, which are the pieces that survive longest.
      ctx.strokeStyle = ctx.fillStyle;
      ctx.lineWidth = rng.range(1.6, 2.6);
      ctx.beginPath();
      ctx.arc(0, 0, rng.range(3, 7), 0, rng.range(1.4, 3.4));
      ctx.stroke();
    }
    ctx.restore();
  }

  // Fade to nothing at the edge so the patch has no border of its own.
  const mask = ctx.createRadialGradient(SIZE / 2, SIZE / 2, SIZE * 0.18, SIZE / 2, SIZE / 2, SIZE * 0.5);
  mask.addColorStop(0, 'rgba(255,255,255,1)');
  mask.addColorStop(0.7, 'rgba(255,255,255,0.9)');
  mask.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.save();
  ctx.globalCompositeOperation = 'destination-in';
  ctx.fillStyle = mask;
  ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.restore();

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
